import { isEqual, mapValues, pick } from 'lodash-es'

import {
  determineMembers,
  formatDurationValues,
  getDurationInitialValues,
  membersToServerDataV3,
} from 'components/event_type_edit'
import { CALLIE_EVENT_TYPE } from 'constants/event_types'
import {
  getAvailabilityInitialValues,
  getDoubleBookingRulesInitialValues,
} from 'features/event_type_editor'
import { withKindsAndTypes } from 'services/event_types'

// The Event Type Editor location adapter consumes the monolith's snake_case model.
// Callie's query is already camel-cased, so only its form-only defaults are added here.
// `nanoId` is what location_v3 keys and removes rows by, so unsaved rows need one too.
const buildLocationConfiguration = (location, index) => ({
  ...location,
  ...(location.id ? {} : { nanoId: `location-${index}` }),
  phoneNumber: location.phoneNumber || '',
  additionalInfo: location.additionalInfo || '',
  hideLocation: location.hideLocation ? 'hidden' : 'visible',
})

// A CallieEventType is a constrained single-host one-on-one Event Type. The Duration
// and Location components read this shape off a plain prop, so a stub built from the
// Callie payload is enough — no EventTypeProvider or membership data required.
export const buildCallieEventTypeStub = (callieEventType) => {
  if (!callieEventType) return undefined

  // The defaults describe the only shape Callie exposes today, but the payload wins so
  // the form follows the model rather than this file if that ever changes.
  return withKindsAndTypes({
    id: callieEventType.id ?? null,
    uuid: callieEventType.uuid,
    kind: callieEventType.kind ?? 'solo',
    kind_name: callieEventType.kindName ?? 'one_on_one',
    owner_type: callieEventType.ownerType ?? 'User',
    type: callieEventType.type ?? CALLIE_EVENT_TYPE,
    booking_method: callieEventType.bookingMethod ?? 'instant',
    pooling_type: callieEventType.poolingType ?? null,
    duration: callieEventType.duration,
    expected_calendar_pusher_membership_id:
      callieEventType.expectedCalendarPusherMembershipId ?? null,
  })
}

// location_v3 reads conferencing status off the host membership, keyed by the monolith's
// column names, while root-api camelCases every response — so the query's membership has
// to be mapped back. An absent flag reads as configured because an integration whose
// status the endpoint does not report must not warn.
const CONFERENCING_STATUS_FIELDS = {
  google_conferencing_configured: 'googleConferencingConfigured',
  is_zoom_configured: 'isZoomConfigured',
  is_microsoft_teams_configured: 'isMicrosoftTeamsConfigured',
  is_webex_configured: 'isWebexConfigured',
  is_gotomeeting_configured: 'isGotomeetingConfigured',
}

// determineMembers reads matching_criteria.table.event_name because the monolith's presenter
// serializes an OpenStruct. The Callie blueprint flattens it, so the `table` hop is added back
// here rather than teaching the shared helper about a second shape.
const extractMatchingCriteriaFields = (matchingCriteria = {}) => {
  const source = matchingCriteria.table ?? matchingCriteria

  return {
    eventName: source.eventName ?? source.event_name ?? '',
    type: source.type ?? matchingCriteria.type,
  }
}

const buildDoubleBookingRule = ({ id, matchingCriteria }) => {
  const { eventName, type } = extractMatchingCriteriaFields(matchingCriteria)

  return {
    id,
    matching_criteria: {
      table: { event_name: eventName, type },
    },
  }
}

// `user_uuid` is what resolves the integration warning to the first person and swaps the
// host's name for a connect button. The availability fields mirror the monolith's membership
// presenter, which is the shape determineMembers and the schedule picker expect.
const buildCallieMembership = (membership) => ({
  ...mapValues(CONFERENCING_STATUS_FIELDS, (field) => membership[field] ?? true),
  id: membership.id ?? null,
  user_id: membership.userId ?? null,
  user_uuid: membership.userUuid ?? null,
  name: membership.name ?? null,
  email: membership.email ?? null,
  enabled: true,
  location_organizer: true,
  is_pending: false,
  active_availability_rule_id: membership.activeAvailabilityRuleId ?? null,
  primary_availability_rule_id: membership.primaryAvailabilityRuleId ?? null,
  own_availability_rule: membership.ownAvailabilityRule ?? null,
  owner_availability_rules: membership.ownerAvailabilityRules ?? [],
  double_booking_rules: (membership.doubleBookingRules || []).map(buildDoubleBookingRule),
})

export const buildCallieFormValues = (callieEventType, defaultAvailabilityRules) => {
  if (!callieEventType) return undefined

  const [membership] = callieEventType.memberships || []
  const memberships = membership ? [buildCallieMembership(membership)] : []
  const eventTypeStub = { ...buildCallieEventTypeStub(callieEventType), memberships }
  const defaultTimezone = defaultAvailabilityRules?.timezone

  return {
    ...getDurationInitialValues({
      duration: callieEventType.duration,
      kind: 'solo',
    }),
    locationConfigurations: (callieEventType.locationConfigurations || [])
      .map(buildLocationConfiguration),
    // location_v3 resolves ET-level vs membership-level locations from these three
    // values; a Callie event type is always ET-level with a single host.
    pooling_type: null,
    memberships,
    userPools: [],
    // getAvailabilityInitialValues also returns booking-range and notice-period values for
    // fields this form does not render, so only the membership slice is taken.
    event_type_memberships_attributes: getAvailabilityInitialValues({
      eventType: eventTypeStub,
      defaultAvailabilityRules: defaultAvailabilityRules || {},
    }).event_type_memberships_attributes,
    ...getDoubleBookingRulesInitialValues(
      Object.values(determineMembers(eventTypeStub, defaultTimezone))
    ),
  }
}

// The standard Event Type formatter emits additional fields that Callie's strong
// params reject, so this serializer deliberately mirrors Callie's smaller allowlist.
const serializeLocationConfiguration = ({
  id,
  kind,
  position,
  location,
  phoneNumber,
  additionalInfo,
  hideLocation,
  organizerUserId,
  _destroy,
}) => ({
  ...(typeof id === 'number' ? { id } : {}),
  ...(kind ? { kind } : {}),
  ...(position != null ? { position } : {}),
  ...(location ? { location } : {}),
  ...(phoneNumber ? { phone_number: phoneNumber } : {}),
  ...(additionalInfo ? { additional_info: additionalInfo } : {}),
  // Sent ahead of the endpoint permitting it; `false` is meaningful, so this cannot
  // use the truthy spread the other keys rely on.
  ...(hideLocation !== undefined ? { hide_location: hideLocation === 'hidden' } : {}),
  ...(organizerUserId ? { organizer_user_id: organizerUserId } : {}),
  ...(_destroy ? { _destroy: true } : {}),
})

// The schedule picker and the weekly-hours editor both write to the membership form object,
// so a change to any of these means the availability payload has to be resent.
const MEMBERSHIP_AVAILABILITY_KEYS = [
  'usingCustomHours',
  'usingSecondarySchedule',
  'activeAvailabilityRuleId',
  'customRuleId',
  'availableHours',
  'dateOverrides',
  'timezone',
]

const getDoubleBookingRules = (formValues) => (
  formValues?.doubleBookingRuleMemberships?.[0]?.doubleBookingRules || []
)

// Callie speaks the same membership-nested shape as the Event Type editor, so the shared
// serializer output is passed through — narrowed to the two keys Callie's strong params
// permit, the same discipline serializeLocationConfiguration follows.
const buildMembershipAttributes = (values, baselineValues) => {
  const [membership] = values.event_type_memberships_attributes || []
  const [baselineMembership] = baselineValues.event_type_memberships_attributes || []
  if (!membership || !baselineMembership) return null

  const doubleBookingRules = getDoubleBookingRules(values)
  const availabilityChanged = !isEqual(
    pick(membership, MEMBERSHIP_AVAILABILITY_KEYS),
    pick(baselineMembership, MEMBERSHIP_AVAILABILITY_KEYS)
  )
  const doubleBookingChanged = !isEqual(doubleBookingRules, getDoubleBookingRules(baselineValues))
  if (!availabilityChanged && !doubleBookingChanged) return null

  const [serialized] = membersToServerDataV3([{ ...membership, doubleBookingRules }])
    .event_type_memberships_attributes

  return pick(serialized, [
    'availability_rule_reference_attributes',
    'availability_rule_attributes',
  ])
}

export const buildCallieEventTypePatch = (values, baselineValues) => {
  // Callie fields only exist on the form when the event type loaded, so without them
  // there is nothing to diff — formatDurationValues would otherwise yield NaN.
  if (values?.eventDuration === undefined || baselineValues?.eventDuration === undefined) {
    return {}
  }

  const attributes = {}
  const { duration } = formatDurationValues(values)
  const { duration: baselineDuration } = formatDurationValues(baselineValues)

  if (duration !== baselineDuration) {
    attributes.duration = duration
  }

  if (!isEqual(values.locationConfigurations, baselineValues.locationConfigurations)) {
    attributes.location_configurations_attributes = values.locationConfigurations
      .map(serializeLocationConfiguration)
  }

  const membershipAttributes = buildMembershipAttributes(values, baselineValues)
  if (membershipAttributes) {
    attributes.event_type_memberships_attributes = [membershipAttributes]
  }

  return attributes
}
